import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { useToast } from './Toast';
import { useAuth } from '../auth/AuthContext';
import { extractErrorMessage } from '../utils/errors';

const API_URL = process.env.REACT_APP_API_URL || '/api';

// ── Types ─────────────────────────────────────────────────────────────────────

type VmPowerState = 'Running' | 'Stopped' | 'Deallocated' | 'Starting' | 'Stopping' | 'Deallocating' | 'Unknown';
type VmPowerAction = 'Start' | 'Stop' | 'Restart' | 'Deallocate';

interface VmDto {
  id: string;
  name: string;
  resourceGroup: string;
  subscriptionId: string;
  subscriptionName: string;
  location: string;
  vmSize: string;
  vCpuCount: number;
  memoryGb: number;
  osType: string;
  osDiskType: string;
  osDiskSizeGb: number;
  dataDiskCount: number;
  powerState: VmPowerState;
  privateIpAddress?: string;
  publicIpAddress?: string;
  networkInterfaces: string[];
  associatedNsgId?: string;
  associatedNsgName?: string;
  lastModified?: string;
  lastStarted?: string;
  tags: Record<string, string>;
}

interface VmMetricsDto {
  vmId: string;
  vmName: string;
  cpuPercent: number;
  memoryPercent: number;
  diskReadBytesPerSec: number;
  diskWriteBytesPerSec: number;
  networkInBytesPerSec: number;
  networkOutBytesPerSec: number;
  timestamp: string;
}

interface VmPowerActionResult {
  totalVms: number;
  successCount: number;
  failureCount: number;
  dryRun: boolean;
  action: VmPowerAction;
  results: Array<{
    vmId: string;
    vmName: string;
    action: VmPowerAction;
    success: boolean;
    error?: string;
    newState?: VmPowerState;
  }>;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const STATE_COLOR: Record<VmPowerState, string> = {
  Running: '#28a745',
  Stopped: '#ffc107',
  Deallocated: '#6c757d',
  Starting: '#17a2b8',
  Stopping: '#fd7e14',
  Deallocating: '#fd7e14',
  Unknown: '#6c757d',
};

const STATE_ICON: Record<VmPowerState, string> = {
  Running: '●',
  Stopped: '■',
  Deallocated: '○',
  Starting: '◐',
  Stopping: '◑',
  Deallocating: '◑',
  Unknown: '?',
};

function formatBytes(bytes: number): string {
  if (bytes >= 1_000_000) return `${(bytes / 1_000_000).toFixed(1)} MB/s`;
  if (bytes >= 1_000) return `${(bytes / 1_000).toFixed(0)} KB/s`;
  return `${bytes} B/s`;
}

function MetricBar({ value, color }: { value: number; color: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
      <div style={{
        flex: 1, height: '6px', background: '#333', borderRadius: '3px', overflow: 'hidden'
      }}>
        <div style={{
          width: `${Math.min(value, 100)}%`,
          height: '100%',
          background: value > 80 ? '#dc3545' : value > 60 ? '#ffc107' : color,
          borderRadius: '3px',
          transition: 'width 0.5s ease'
        }} />
      </div>
      <span style={{ fontSize: '0.8rem', color: '#b0b0b0', minWidth: '38px', textAlign: 'right' }}>
        {value.toFixed(0)}%
      </span>
    </div>
  );
}

// ── Component ─────────────────────────────────────────────────────────────────

const VmManager: React.FC = () => {
  const { user } = useAuth();
  // Mirrors the API policy: VM power actions need operator or admin.
  const canWrite = user?.role === 'operator' || user?.role === 'admin';
  const { showToast } = useToast();
  const [vms, setVms] = useState<VmDto[]>([]);
  const [metrics, setMetrics] = useState<Record<string, VmMetricsDto>>({});
  const [selectedVms, setSelectedVms] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [metricsLoading, setMetricsLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [lastResult, setLastResult] = useState<VmPowerActionResult | null>(null);
  const [dryRun, setDryRun] = useState(true);

  // Filters
  const [filterSub, setFilterSub] = useState('');
  const [filterState, setFilterState] = useState('');
  const [filterRg, setFilterRg] = useState('');
  const [searchText, setSearchText] = useState('');

  // Detail panel
  const [expandedVmId, setExpandedVmId] = useState<string | null>(null);

  const loadVms = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/vm`, { params: { subscriptionId: 'all' } });
      setVms(res.data);
    } catch (err: any) {
      showToast('Failed to load VMs.', 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  const loadMetrics = useCallback(async () => {
    setMetricsLoading(true);
    try {
      const res = await axios.get(`${API_URL}/vm/metrics`, { params: { subscriptionId: 'all' } });
      const map: Record<string, VmMetricsDto> = {};
      for (const m of res.data as VmMetricsDto[]) map[m.vmId] = m;
      setMetrics(map);
    } catch {
      // metrics are optional — silently ignore
    } finally {
      setMetricsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadVms();
  }, [loadVms]);

  useEffect(() => {
    if (vms.length > 0) loadMetrics();
  }, [vms.length, loadMetrics]);

  // Auto-refresh metrics every 30s
  useEffect(() => {
    const id = setInterval(loadMetrics, 30_000);
    return () => clearInterval(id);
  }, [loadMetrics]);

  // ── Derived data ──────────────────────────────────────────────────────────

  const uniqueSubs = useMemo(() => Array.from(new Set(vms.map(v => v.subscriptionName))).sort(), [vms]);
  const uniqueRgs = useMemo(() => Array.from(new Set(vms.map(v => v.resourceGroup))).sort(), [vms]);

  const filteredVms = useMemo(() => {
    return vms.filter(v => {
      if (filterSub && v.subscriptionName !== filterSub) return false;
      if (filterState && v.powerState !== filterState) return false;
      if (filterRg && v.resourceGroup !== filterRg) return false;
      if (searchText) {
        const q = searchText.toLowerCase();
        if (!v.name.toLowerCase().includes(q) &&
            !v.vmSize.toLowerCase().includes(q) &&
            !v.resourceGroup.toLowerCase().includes(q)) return false;
      }
      return true;
    });
  }, [vms, filterSub, filterState, filterRg, searchText]);

  const stateCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const v of vms) counts[v.powerState] = (counts[v.powerState] ?? 0) + 1;
    return counts;
  }, [vms]);

  // ── Selection ─────────────────────────────────────────────────────────────

  const toggleVm = (id: string) => {
    const next = new Set(selectedVms);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelectedVms(next);
  };

  const selectAll = () => {
    if (selectedVms.size === filteredVms.length) setSelectedVms(new Set());
    else setSelectedVms(new Set(filteredVms.map(v => v.id)));
  };

  // ── Power actions ─────────────────────────────────────────────────────────

  const executePowerAction = async (action: VmPowerAction) => {
    if (selectedVms.size === 0) { showToast('Select at least one VM.', 'warning'); return; }
    setActionLoading(true);
    try {
      const res = await axios.post(`${API_URL}/vm/power`, {
        vmIds: Array.from(selectedVms),
        action,
        dryRun
      });
      if (res.data.approvalRequired) {
        showToast(`${action} submitted for approval — track it in the Approvals tab.`, 'info');
        return;
      }
      const result: VmPowerActionResult = res.data;
      setLastResult(result);

      const prefix = dryRun ? '[DRY RUN] ' : '';
      if (result.failureCount === 0) {
        showToast(`${prefix}${action} completed for ${result.successCount} VM(s).`, 'success');
      } else {
        showToast(`${prefix}${action}: ${result.successCount} succeeded, ${result.failureCount} failed.`, 'warning');
      }

      if (!dryRun) await loadVms();
    } catch (err: any) {
      showToast(`${action} failed: ${extractErrorMessage(err)}`, 'error');
    } finally {
      setActionLoading(false);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div>
      <style>{`
        .vm-summary-bar { display: flex; gap: 1rem; flex-wrap: wrap; margin-bottom: 1.5rem; }
        .vm-summary-card {
          background: #25252a; border: 1px solid #444; border-radius: 8px;
          padding: 0.75rem 1.25rem; min-width: 120px;
          display: flex; flex-direction: column; align-items: center; gap: 0.25rem;
        }
        .vm-summary-card .count { font-size: 1.75rem; font-weight: 700; }
        .vm-summary-card .label { font-size: 0.8rem; color: #b0b0b0; text-transform: uppercase; letter-spacing: 0.05em; }
        .vm-toolbar { display: flex; flex-wrap: wrap; gap: 0.75rem; align-items: center; background: #25252a; padding: 0.75rem 1rem; border-radius: 6px; margin-bottom: 1rem; }
        .vm-action-btn { padding: 0.4rem 0.9rem; border: none; border-radius: 5px; cursor: pointer; font-size: 0.875rem; font-weight: 600; transition: all 0.2s; }
        .vm-action-btn:disabled { opacity: 0.4; cursor: not-allowed; }
        .vm-table-wrap { background: #25252a; border-radius: 8px; overflow: hidden; border: 1px solid #444; }
        .vm-table { width: 100%; border-collapse: collapse; font-size: 0.875rem; }
        .vm-table th { background: #333338; padding: 0.75rem 1rem; text-align: left; font-weight: 600; color: #b0b0b0; font-size: 0.8rem; text-transform: uppercase; letter-spacing: 0.04em; border-bottom: 1px solid #444; }
        .vm-table td { padding: 0.7rem 1rem; border-bottom: 1px solid #333; vertical-align: middle; }
        .vm-table tr:last-child td { border-bottom: none; }
        .vm-table tr:hover td { background: #2f2f35; }
        .vm-table tr.selected td { background: #007bff15; }
        .vm-table tr.expanded td { background: #1e1e25; }
        .power-badge { display: inline-flex; align-items: center; gap: 0.35rem; padding: 0.2rem 0.55rem; border-radius: 12px; font-size: 0.775rem; font-weight: 600; }
        .vm-detail-row td { padding: 0 !important; }
        .vm-detail-panel { background: #1a1a1e; border-top: 1px solid #333; padding: 1.25rem 1.5rem; display: grid; grid-template-columns: repeat(auto-fill, minmax(220px, 1fr)); gap: 1rem; }
        .detail-group h4 { margin: 0 0 0.5rem; font-size: 0.8rem; color: #b0b0b0; text-transform: uppercase; letter-spacing: 0.04em; }
        .detail-group p { margin: 0 0 0.25rem; font-size: 0.875rem; }
        .tag-chip { display: inline-block; background: #444; padding: 0.15rem 0.45rem; border-radius: 4px; font-size: 0.775rem; margin: 0.1rem; }
        .result-panel { background: #25252a; border: 1px solid #444; border-radius: 8px; padding: 1rem 1.25rem; margin-bottom: 1rem; }
        .result-panel h4 { margin: 0 0 0.75rem; font-size: 0.95rem; }
        .result-item { display: flex; align-items: center; gap: 0.5rem; padding: 0.3rem 0; font-size: 0.85rem; border-bottom: 1px solid #333; }
        .result-item:last-child { border-bottom: none; }
        .filter-row { display: flex; flex-wrap: wrap; gap: 0.75rem; align-items: center; }
        .filter-row select, .filter-row input { background: #1a1a1e; color: #f0f0f0; border: 1px solid #444; border-radius: 5px; padding: 0.4rem 0.65rem; font-size: 0.875rem; }
      `}</style>

      {/* Summary bar */}
      <div className="vm-summary-bar">
        <div className="vm-summary-card">
          <span className="count">{vms.length}</span>
          <span className="label">Total VMs</span>
        </div>
        {(['Running', 'Stopped', 'Deallocated'] as VmPowerState[]).map(state => (
          <div key={state} className="vm-summary-card"
            style={{ borderColor: STATE_COLOR[state] + '66', cursor: 'pointer' }}
            onClick={() => setFilterState(filterState === state ? '' : state)}
          >
            <span className="count" style={{ color: STATE_COLOR[state] }}>
              {stateCounts[state] ?? 0}
            </span>
            <span className="label">{state}</span>
          </div>
        ))}
        <div className="vm-summary-card" style={{ borderColor: '#17a2b855' }}>
          <span className="count" style={{ color: '#17a2b8' }}>
            {vms.reduce((s, v) => s + v.vCpuCount, 0)}
          </span>
          <span className="label">Total vCPUs</span>
        </div>
        <div className="vm-summary-card" style={{ borderColor: '#17a2b855' }}>
          <span className="count" style={{ color: '#17a2b8' }}>
            {vms.reduce((s, v) => s + v.memoryGb, 0).toFixed(0)} GB
          </span>
          <span className="label">Total RAM</span>
        </div>
        <button
          onClick={loadVms}
          disabled={loading}
          style={{ marginLeft: 'auto', background: '#007bff', color: 'white', border: 'none', borderRadius: '6px', padding: '0.5rem 1rem', cursor: 'pointer', fontWeight: 600 }}
        >
          {loading ? 'Loading…' : '🔄 Refresh'}
        </button>
      </div>

      {/* Filters */}
      <div style={{ marginBottom: '1rem' }}>
        <div className="filter-row">
          <input
            type="text"
            placeholder="Search VMs…"
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
            style={{ minWidth: '200px' }}
          />
          <select value={filterSub} onChange={e => setFilterSub(e.target.value)}>
            <option value="">All Subscriptions</option>
            {uniqueSubs.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <select value={filterState} onChange={e => setFilterState(e.target.value)}>
            <option value="">All States</option>
            {(['Running', 'Stopped', 'Deallocated', 'Unknown'] as VmPowerState[]).map(s => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
          <select value={filterRg} onChange={e => setFilterRg(e.target.value)}>
            <option value="">All Resource Groups</option>
            {uniqueRgs.map(rg => <option key={rg} value={rg}>{rg}</option>)}
          </select>
          {(filterSub || filterState || filterRg || searchText) && (
            <button
              onClick={() => { setFilterSub(''); setFilterState(''); setFilterRg(''); setSearchText(''); }}
              style={{ background: '#ffc107', color: '#333', border: 'none', borderRadius: '5px', padding: '0.4rem 0.75rem', cursor: 'pointer', fontWeight: 600 }}
            >
              Clear
            </button>
          )}
          <span style={{ marginLeft: 'auto', color: '#b0b0b0', fontSize: '0.875rem' }}>
            {selectedVms.size} of {filteredVms.length} selected
          </span>
        </div>
      </div>

      {/* Action toolbar */}
      {canWrite && (
      <div className="vm-toolbar">
        <span style={{ fontWeight: 600, color: '#b0b0b0', fontSize: '0.875rem' }}>Bulk Actions:</span>
        {([
          { action: 'Start' as VmPowerAction, color: '#28a745', label: '▶ Start' },
          { action: 'Stop' as VmPowerAction, color: '#ffc107', label: '■ Stop' },
          { action: 'Restart' as VmPowerAction, color: '#17a2b8', label: '↺ Restart' },
          { action: 'Deallocate' as VmPowerAction, color: '#6c757d', label: '○ Deallocate' },
        ]).map(({ action, color, label }) => (
          <button
            key={action}
            className="vm-action-btn"
            style={{ background: color, color: color === '#ffc107' ? '#333' : 'white' }}
            disabled={selectedVms.size === 0 || actionLoading}
            onClick={() => executePowerAction(action)}
          >
            {actionLoading ? '…' : label}
          </button>
        ))}
        <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer', color: '#f0f0f0', fontSize: '0.875rem', marginLeft: 'auto' }}>
          <input
            type="checkbox"
            checked={dryRun}
            onChange={e => setDryRun(e.target.checked)}
            style={{ cursor: 'pointer' }}
          />
          Dry Run
        </label>
      </div>
      )}

      {/* Last action result */}
      {lastResult && (
        <div className="result-panel" style={{ marginBottom: '1rem' }}>
          <h4>
            {lastResult.dryRun ? '🔍 Dry Run — ' : ''}
            {lastResult.action} Result: {lastResult.successCount} succeeded
            {lastResult.failureCount > 0 && `, ${lastResult.failureCount} failed`}
          </h4>
          {lastResult.results.map(r => (
            <div key={r.vmId} className="result-item">
              <span style={{ color: r.success ? '#28a745' : '#dc3545' }}>
                {r.success ? '✓' : '✗'}
              </span>
              <span style={{ fontWeight: 600 }}>{r.vmName}</span>
              {r.newState && (
                <span style={{ color: STATE_COLOR[r.newState], fontSize: '0.8rem' }}>
                  → {r.newState}
                </span>
              )}
              {r.error && <span style={{ color: '#ff8f9a', fontSize: '0.8rem' }}>{r.error}</span>}
            </div>
          ))}
        </div>
      )}

      {/* VM Table */}
      <div className="vm-table-wrap">
        <table className="vm-table">
          <thead>
            <tr>
              <th style={{ width: '36px' }}>
                <input
                  type="checkbox"
                  checked={filteredVms.length > 0 && selectedVms.size === filteredVms.length}
                  onChange={selectAll}
                />
              </th>
              <th>Name</th>
              <th>State</th>
              <th>Size</th>
              <th>OS</th>
              <th>Resource Group</th>
              <th>IP Address</th>
              <th>CPU</th>
              <th>Memory</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={10} style={{ textAlign: 'center', padding: '2rem', color: '#b0b0b0' }}>
                  Loading VMs…
                </td>
              </tr>
            )}
            {!loading && filteredVms.length === 0 && (
              <tr>
                <td colSpan={10} style={{ textAlign: 'center', padding: '2rem', color: '#b0b0b0' }}>
                  No VMs match the current filters.
                </td>
              </tr>
            )}
            {filteredVms.map(vm => {
              const m = metrics[vm.id];
              const isExpanded = expandedVmId === vm.id;
              return (
                <React.Fragment key={vm.id}>
                  <tr
                    className={[
                      selectedVms.has(vm.id) ? 'selected' : '',
                      isExpanded ? 'expanded' : ''
                    ].join(' ')}
                  >
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedVms.has(vm.id)}
                        onChange={() => toggleVm(vm.id)}
                      />
                    </td>
                    <td>
                      <div style={{ fontWeight: 600 }}>{vm.name}</div>
                      <div style={{ fontSize: '0.75rem', color: '#b0b0b0' }}>{vm.subscriptionName}</div>
                    </td>
                    <td>
                      <span
                        className="power-badge"
                        style={{
                          background: STATE_COLOR[vm.powerState] + '22',
                          color: STATE_COLOR[vm.powerState],
                          border: `1px solid ${STATE_COLOR[vm.powerState]}55`
                        }}
                      >
                        {STATE_ICON[vm.powerState]} {vm.powerState}
                      </span>
                    </td>
                    <td>
                      <div style={{ fontSize: '0.85rem' }}>{vm.vmSize}</div>
                      <div style={{ fontSize: '0.75rem', color: '#b0b0b0' }}>
                        {vm.vCpuCount} vCPU · {vm.memoryGb} GB
                      </div>
                    </td>
                    <td>
                      <span style={{ fontSize: '0.85rem' }}>{vm.osType}</span>
                    </td>
                    <td style={{ fontSize: '0.85rem' }}>{vm.resourceGroup}</td>
                    <td style={{ fontSize: '0.85rem', fontFamily: 'monospace' }}>
                      {vm.privateIpAddress ?? '—'}
                    </td>
                    <td style={{ minWidth: '100px' }}>
                      {m ? (
                        <MetricBar value={m.cpuPercent} color="#007bff" />
                      ) : vm.powerState === 'Running' ? (
                        <span style={{ color: '#555', fontSize: '0.75rem' }}>
                          {metricsLoading ? 'loading…' : '—'}
                        </span>
                      ) : (
                        <span style={{ color: '#555', fontSize: '0.75rem' }}>offline</span>
                      )}
                    </td>
                    <td style={{ minWidth: '100px' }}>
                      {m ? (
                        <MetricBar value={m.memoryPercent} color="#17a2b8" />
                      ) : vm.powerState === 'Running' ? (
                        <span style={{ color: '#555', fontSize: '0.75rem' }}>
                          {metricsLoading ? 'loading…' : '—'}
                        </span>
                      ) : (
                        <span style={{ color: '#555', fontSize: '0.75rem' }}>offline</span>
                      )}
                    </td>
                    <td>
                      <button
                        onClick={() => setExpandedVmId(isExpanded ? null : vm.id)}
                        style={{
                          background: 'none', border: '1px solid #444', color: '#b0b0b0',
                          borderRadius: '4px', cursor: 'pointer', padding: '0.2rem 0.5rem',
                          fontSize: '0.8rem', transition: 'all 0.2s'
                        }}
                      >
                        {isExpanded ? '▲' : '▼'}
                      </button>
                    </td>
                  </tr>

                  {/* Detail panel */}
                  {isExpanded && (
                    <tr className="vm-detail-row">
                      <td colSpan={10}>
                        <div className="vm-detail-panel">
                          <div className="detail-group">
                            <h4>Compute</h4>
                            <p>Size: <strong>{vm.vmSize}</strong></p>
                            <p>vCPUs: <strong>{vm.vCpuCount}</strong></p>
                            <p>Memory: <strong>{vm.memoryGb} GB</strong></p>
                            <p>OS Type: <strong>{vm.osType}</strong></p>
                          </div>
                          <div className="detail-group">
                            <h4>Storage</h4>
                            <p>OS Disk: <strong>{vm.osDiskSizeGb} GB</strong> ({vm.osDiskType})</p>
                            <p>Data Disks: <strong>{vm.dataDiskCount}</strong></p>
                          </div>
                          <div className="detail-group">
                            <h4>Network</h4>
                            <p>Private IP: <strong style={{ fontFamily: 'monospace' }}>{vm.privateIpAddress ?? '—'}</strong></p>
                            <p>Public IP: <strong style={{ fontFamily: 'monospace' }}>{vm.publicIpAddress ?? 'None'}</strong></p>
                            <p>NIC: <strong>{vm.networkInterfaces.join(', ')}</strong></p>
                            {vm.associatedNsgName && (
                              <p>NSG: <strong>{vm.associatedNsgName}</strong></p>
                            )}
                          </div>
                          <div className="detail-group">
                            <h4>Location</h4>
                            <p>Region: <strong>{vm.location}</strong></p>
                            <p>RG: <strong>{vm.resourceGroup}</strong></p>
                            <p>Subscription: <strong>{vm.subscriptionName}</strong></p>
                          </div>
                          {m && (
                            <div className="detail-group">
                              <h4>Live Metrics</h4>
                              <p style={{ marginBottom: '0.5rem' }}>Disk R: <strong>{formatBytes(m.diskReadBytesPerSec)}</strong></p>
                              <p style={{ marginBottom: '0.5rem' }}>Disk W: <strong>{formatBytes(m.diskWriteBytesPerSec)}</strong></p>
                              <p style={{ marginBottom: '0.5rem' }}>Net In: <strong>{formatBytes(m.networkInBytesPerSec)}</strong></p>
                              <p>Net Out: <strong>{formatBytes(m.networkOutBytesPerSec)}</strong></p>
                            </div>
                          )}
                          <div className="detail-group">
                            <h4>Tags</h4>
                            <div>
                              {Object.entries(vm.tags).length === 0
                                ? <span style={{ color: '#b0b0b0', fontSize: '0.85rem' }}>No tags</span>
                                : Object.entries(vm.tags).map(([k, v]) => (
                                  <span key={k} className="tag-chip">{k}: {v}</span>
                                ))}
                            </div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default VmManager;
